import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '../../assets/Logo.svg';
import './Calendar.css';
import { useRealtimeAccessGuard } from '../../lib/useRealtimeAccessGuard';
import AccessGuardOverlay from '../../components/AccessGuardOverlay/AccessGuardOverlay';
import { fetchVacationsInRange, getVacationReasonForISO, toISODateOnly } from '../../lib/schoolVacations';
import { supabase } from '../../lib/supabase';
import {
  createAssignments,
  deleteAssignment,
  fetchAssignmentsInRange,
  fetchGradesForSchool,
  fetchGradeSectionsForSchool,
  getAttachmentPublicUrl,
  getSessionUser,
  isValidUrl,
  openExternalUrl,
  toAbsoluteUrl,
  toISODate,
  updateAssignment,
  uploadAssignmentAttachment,
} from '../../lib/calendarAssignments';

const Calendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [calendarView, setCalendarView] = useState('week'); // 'week' or 'month'

  const [grades, setGrades] = useState([]);
  const [gradeSections, setGradeSections] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [schoolName, setSchoolName] = useState(null);

  // Teacher filter: "all" or a grade_id
  const [teacherGradeFilter, setTeacherGradeFilter] = useState('all');

  // Modal state
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [editingAssignment, setEditingAssignment] = useState(null); // assignment row being edited
  const [assignmentForm, setAssignmentForm] = useState({
    title: '',
    description: '',
    assigned_date: '',
    link_url: '',
    selectedKeys: [], // Array of keys like "gradeId" or "gradeId:sectionId"
  });
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [removeExistingAttachment, setRemoveExistingAttachment] = useState(false);

  // Day overlay (view details)
  const [showDayOverlay, setShowDayOverlay] = useState(false);
  const [dayOverlayDate, setDayOverlayDate] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Realtime access guard: kick user out if their account is deactivated or school subscription becomes inactive.
  const { blockInfo, acknowledge } = useRealtimeAccessGuard({
    userId: currentUser?.id || null,
    schoolId: currentUser?.school_id || null,
  });

  // Navigation items
  const navItems = [
    { label: 'Home', path: '/home' },
    { label: 'Calendar', path: '/calendar' },
    { label: 'Profile', path: '/profile' },
  ];

  // Get month and year for display
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Fetch current user to check role
  const fetchCurrentUser = async () => {
    const user = getSessionUser();
    if (!user) {
      window.location.href = '/';
      return;
    }
    setCurrentUser(user);
    
    // Fetch school name
    try {
      const userDataStr = sessionStorage.getItem('currentUser');
      if (userDataStr) {
        const userData = JSON.parse(userDataStr);
        if (userData.schools?.name) {
          setSchoolName(userData.schools.name);
        } else if (user.school_id) {
          const { data: schoolData } = await supabase
            .from('schools')
            .select('name')
            .eq('id', user.school_id)
            .single();
          if (schoolData?.name) {
            setSchoolName(schoolData.name);
          }
        }
      }
    } catch (err) {
      }
  };

  // Check if user is a teacher
  const isTeacher = () => {
    return currentUser && currentUser.role === 'teacher';
  };

  const isStudent = () => {
    return currentUser && currentUser.role === 'student';
  };

  const schoolId = currentUser?.school_id || null;

  const isVacationISO = useMemo(() => {
    const ranges = (vacations || [])
      .map(v => ({ start: toISODateOnly(v.start_date), end: toISODateOnly(v.end_date) }))
      .filter(v => v.start && v.end);
    return (iso) => {
      if (!iso) return false;
      const isoDay = toISODateOnly(iso);
      return ranges.some(r => isoDay >= r.start && isoDay <= r.end);
    };
  }, [vacations]);

  const selectedDateISO = useMemo(() => (selectedDate ? toISODate(selectedDate) : null), [selectedDate]);
  const selectedIsVacation = useMemo(() => (selectedDateISO ? isVacationISO(selectedDateISO) : false), [selectedDateISO, isVacationISO]);
  const selectedVacationReason = useMemo(
    () => (selectedDateISO ? getVacationReasonForISO(vacations, selectedDateISO) : ''),
    [selectedDateISO, vacations]
  );

  // Get days of the current week (Sunday to Saturday)
  const getWeekDays = (date) => {
    const currentDay = date.getDay(); // 0 = Sunday, 6 = Saturday
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - currentDay); // Go to Sunday

    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const renderAttachmentPreview = (a) => {
    if (!a.attachment_path) return null;
    const url = getAttachmentPublicUrl(a.attachment_path, 'assignments');
    if (!url) return null;

    const ext = a.attachment_path.split('.').pop()?.toLowerCase();
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
    const isVideo = ['mp4', 'webm', 'ogg'].includes(ext);

    if (isImage) {
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="assignment-media-preview image-preview"
          title="Click to view full image"
          onClick={(e) => openExternalUrl(url, e)}
        >
          <img src={url} alt={a.attachment_name || 'Preview'} />
        </a>
      );
    }

    if (isVideo) {
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="assignment-media-preview video-preview"
          title="Click to view full video"
          onClick={(e) => openExternalUrl(url, e)}
        >
          <video muted>
            <source src={url} type={`video/${ext === 'ogv' ? 'ogg' : ext}`} />
          </video>
        </a>
      );
    }

    return null;
  };

  const gradeById = useMemo(() => {
    const map = new Map();
    (grades || []).forEach(g => map.set(g.id, g));
    return map;
  }, [grades]);

  const studentGradeId = useMemo(() => {
    if (!currentUser) return null;
    if (currentUser.grade_id) return currentUser.grade_id;
    // Fallback: match on the legacy grade string
    if (currentUser.grade && grades?.length) {
      const exact = grades.find(g => String(g.name).toLowerCase() === String(currentUser.grade).toLowerCase());
      if (exact) return exact.id;
      const contains = grades.find(g => String(g.name).toLowerCase().includes(String(currentUser.grade).toLowerCase()));
      if (contains) return contains.id;
    }
    return null;
  }, [currentUser, grades]);

  // Student's specific section (if assigned)
  const studentSectionId = useMemo(() => {
    if (!currentUser) return null;
    return currentUser.grade_section_id || null;
  }, [currentUser]);

  // Parse teachers_grade_ids JSONB array into structured data
  // Returns array of { gradeId, sectionId, key } objects
  const teacherAssignments = useMemo(() => {
    if (!currentUser || currentUser.role !== 'teacher') return [];

    // Try the new teachers_grade_ids JSONB field first
    if (currentUser.teachers_grade_ids && Array.isArray(currentUser.teachers_grade_ids) && currentUser.teachers_grade_ids.length > 0) {
      return currentUser.teachers_grade_ids.map(item => {
        if (typeof item === 'string') {
          // Legacy format: just grade_id as string
          return { gradeId: item, sectionId: null, key: item };
        } else if (typeof item === 'object' && item.grade_id) {
          // New format: object with grade_id and optional section_id
          const sectionId = item.section_id || null;
          const key = sectionId ? `${item.grade_id}:${sectionId}` : item.grade_id;
          return { gradeId: item.grade_id, sectionId, key };
        }
        return null;
      }).filter(Boolean);
    }

    // Fallback: try to parse from legacy grade string
    const raw = (currentUser.grade || '').trim();
    if (!raw) return [];

    const taughtNames = raw
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
      .map(s => s.toLowerCase());

    return grades
      .filter(g => taughtNames.includes(String(g.name).toLowerCase()))
      .map(g => ({ gradeId: g.id, sectionId: null, key: g.id }));
  }, [currentUser, grades]);

  // Unique grade IDs for filtering/fetching
  const teacherTaughtGradeIds = useMemo(() => {
    return [...new Set(teacherAssignments.map(a => a.gradeId))];
  }, [teacherAssignments]);

  const headerLabel = useMemo(() => {
    if (calendarView === 'month') {
      return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    }

    // Week View range
    const weekDays = getWeekDays(selectedDate || currentDate);
    const start = weekDays[0];
    const end = weekDays[6];

    const startDay = start.getDate();
    const endDay = end.getDate();
    const startMonth = monthNames[start.getMonth()].substring(0, 3);
    const endMonth = monthNames[end.getMonth()].substring(0, 3);
    const startYear = start.getFullYear();
    const endYear = end.getFullYear();

    if (startYear !== endYear) {
      return `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`;
    }
    if (start.getMonth() !== end.getMonth()) {
      return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${startYear}`;
    }
    return `${startDay} - ${endDay} ${startMonth} ${startYear}`;
  }, [calendarView, currentDate, selectedDate]);

  // Get sections for a specific grade from teacher's assignments
  const sectionsByGrade = useMemo(() => {
    const map = new Map();
    gradeSections.forEach(s => {
      if (!map.has(s.grade_id)) map.set(s.grade_id, []);
      map.get(s.grade_id).push(s);
    });
    return map;
  }, [gradeSections]);

  // Build grade/section options for the teacher
  // Only shows grades/sections the teacher is assigned to
  const teacherGradeSectionOptions = useMemo(() => {
    if (!isTeacher()) return [];
    if (!teacherAssignments.length) return []; // No assignments configured

    const options = [];
    const addedKeys = new Set(); // Prevent duplicates

    teacherAssignments.forEach(assignment => {
      const grade = gradeById.get(assignment.gradeId);
      if (!grade) return;

      const gradeSectionsForGrade = (sectionsByGrade.get(assignment.gradeId) || [])
        .filter(s => s.is_active !== false)
        .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));

      if (assignment.sectionId) {
        // Teacher is assigned to a SPECIFIC section - show only that section (read-only)
        const section = gradeSections.find(s => s.id === assignment.sectionId);
        const key = assignment.key;
        if (!addedKeys.has(key)) {
          addedKeys.add(key);
          options.push({
            key,
            gradeId: assignment.gradeId,
            sectionId: assignment.sectionId,
            label: section ? `${grade.name} ${section.name}` : `${grade.name} - Unknown Section`,
            isReadOnly: true, // Specific section assignment - can't change
            isAssigned: true,
            isSection: true,
          });
        }
      } else if (gradeSectionsForGrade.length > 0) {
        // Teacher is assigned to GRADE, but grade HAS sections - show all sections as selectable
        gradeSectionsForGrade.forEach(section => {
          const key = `${assignment.gradeId}:${section.id}`;
          if (!addedKeys.has(key)) {
            addedKeys.add(key);
            options.push({
              key,
              gradeId: assignment.gradeId,
              sectionId: section.id,
              label: `${grade.name} ${section.name}`,
              isReadOnly: false, // Teacher can choose which sections
              isAssigned: false,
              isSection: true,
            });
          }
        });
      } else {
        // Grade has no sections - show grade only
        const key = assignment.gradeId;
        if (!addedKeys.has(key)) {
          addedKeys.add(key);
          options.push({
            key,
            gradeId: assignment.gradeId,
            sectionId: null,
            label: grade.name,
            isReadOnly: true, // Assigned to this grade
            isAssigned: true,
            isSection: false,
          });
        }
      }
    });

    // Sort options by label for consistent display
    options.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));

    return options;
  }, [teacherAssignments, gradeById, sectionsByGrade, gradeSections]);

  const teacherGrades = useMemo(() => {
    if (!isTeacher()) return [];
    // If teacher has not been configured, fall back to all grades (so the page still works)
    if (!teacherTaughtGradeIds.length) return grades;
    return grades.filter(g => teacherTaughtGradeIds.includes(g.id));
  }, [grades, teacherTaughtGradeIds, currentUser]);

  const getTeacherDisplayName = (userRow) => {
    const u = userRow?.created_by_user || userRow?.users || userRow;
    if (!u) return 'Teacher';
    return (
      u.full_name ||
      `${u.first_name || ''} ${u.last_name || ''}`.trim() ||
      'Teacher'
    );
  };

  // Get days in month
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  // Navigate
  const goToPrevious = () => {
    if (calendarView === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() - 7);
      setCurrentDate(newDate);
      // If we have a selected date, move it as well to keep focus
      if (selectedDate) {
        const nextSelected = new Date(selectedDate);
        nextSelected.setDate(selectedDate.getDate() - 7);
        setSelectedDate(nextSelected);
      }
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
      setSelectedDate(null);
    }
  };

  const goToNext = () => {
    if (calendarView === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() + 7);
      setCurrentDate(newDate);
      // If we have a selected date, move it as well to keep focus
      if (selectedDate) {
        const nextSelected = new Date(selectedDate);
        nextSelected.setDate(selectedDate.getDate() + 7);
        setSelectedDate(nextSelected);
      }
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
      setSelectedDate(null);
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  // Handle date selection
  const handleDateClick = (dayOrDate) => {
    if (dayOrDate) {
      const clickedDate = dayOrDate instanceof Date
        ? new Date(dayOrDate.getFullYear(), dayOrDate.getMonth(), dayOrDate.getDate())
        : new Date(currentDate.getFullYear(), currentDate.getMonth(), dayOrDate);
      const wasAlreadySelected = selectedDate && clickedDate.toDateString() === selectedDate.toDateString();
      setSelectedDate(clickedDate);

      const key = toISODate(clickedDate);
      const dayAssignments = assignmentsByDate.get(key) || [];
      if (dayAssignments.length > 0) {
        // Ensure only ONE overlay is visible at a time
        setShowAssignmentModal(false);
        setEditingAssignment(null);
        setFormError(null);
        setFormSuccess(null);
        setAttachmentFile(null);
        setDayOverlayDate(clickedDate);
        setShowDayOverlay(true);
        return;
      }

      // Teacher UX: click once to select, click again to add assignment
      if (isTeacher() && wasAlreadySelected) {
        if (isVacationISO(key)) {
          setError('This day is marked as a school vacation/day off. Assignments cannot be added on vacation days.');
          return;
        }
        openAssignmentModal(clickedDate);
      }
    }
  };

  const openAssignmentModal = (date) => {
    // Ensure only ONE overlay is visible at a time
    setShowDayOverlay(false);
    setDayOverlayDate(null);
    const assignedDateISO = date ? toISODate(date) : (selectedDate ? toISODate(selectedDate) : '');
    if (assignedDateISO && isVacationISO(assignedDateISO)) {
      setError('This day is marked as a school vacation/day off. Assignments cannot be added on vacation days.');
      return;
    }

    // Build default selected keys based on teacher's assignments
    const defaultKeys = (() => {
      if (!isTeacher()) return [];

      // If teacher is filtered to one grade/section, use that
      if (teacherGradeFilter && teacherGradeFilter !== 'all') {
        // If filter matches a specific option, use it; otherwise find by gradeId
        const matchingOption = teacherGradeSectionOptions.find(opt => opt.key === teacherGradeFilter);
        if (matchingOption) {
          return [matchingOption.key];
        }
      }

      // Default to all read-only assignments (specific sections) plus one from each grade
      const readOnlyKeys = teacherGradeSectionOptions
        .filter(opt => opt.isReadOnly)
        .map(opt => opt.key);

      return readOnlyKeys.length > 0 ? readOnlyKeys : [];
    })();

    setFormError(null);
    setFormSuccess(null);
    setAttachmentFile(null);
    setEditingAssignment(null);
    setRemoveExistingAttachment(false);
    setAssignmentForm({
      title: '',
      description: '',
      assigned_date: assignedDateISO,
      link_url: '',
      selectedKeys: defaultKeys,
    });
    setShowAssignmentModal(true);
  };

  const openEditAssignmentModal = (assignmentRow) => {
    if (!assignmentRow) return;
    // Teacher cannot edit someone else's assignment
    if (!isTeacher() || assignmentRow.created_by !== currentUser?.id) return;

    // Ensure only ONE overlay is visible at a time
    setShowDayOverlay(false);
    setDayOverlayDate(null);
    setFormError(null);
    setFormSuccess(null);
    setAttachmentFile(null);
    setEditingAssignment(assignmentRow);

    // Build key from existing assignment
    const existingKey = assignmentRow.section_id
      ? `${assignmentRow.grade_id}:${assignmentRow.section_id}`
      : assignmentRow.grade_id;

    setAssignmentForm({
      title: assignmentRow.title || '',
      description: assignmentRow.description || '',
      assigned_date: assignmentRow.assigned_date || (selectedDate ? toISODate(selectedDate) : ''),
      link_url: assignmentRow.link_url || '',
      selectedKeys: [existingKey].filter(Boolean),
    });
    setShowAssignmentModal(true);
  };

  const closeAssignmentModal = () => {
    setShowAssignmentModal(false);
    setFormError(null);
    setFormSuccess(null);
    setUploading(false);
    setSaving(false);
    setAttachmentFile(null);
    setEditingAssignment(null);
    setRemoveExistingAttachment(false);
  };

  const monthRange = useMemo(() => {
    const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    return { startISO: toISODate(start), endISO: toISODate(end) };
  }, [currentDate]);

  // Week view can include days from the previous/next month.
  const vacationRange = useMemo(() => {
    const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    start.setDate(start.getDate() - 6);
    end.setDate(end.getDate() + 6);
    return { startISO: toISODate(start), endISO: toISODate(end) };
  }, [currentDate]);

  const visibleGradeIds = useMemo(() => {
    if (!currentUser) return [];
    if (isStudent()) {
      return studentGradeId ? [studentGradeId] : [];
    }
    if (isTeacher()) {
      // "all" means all grades this teacher teaches (NOT all grades in the school)
      if (teacherGradeFilter === 'all') {
        return teacherTaughtGradeIds.length ? teacherTaughtGradeIds : grades.map(g => g.id);
      }
      // Extract gradeId from filter key (handles both gradeId and gradeId:sectionId)
      const gradeId = teacherGradeFilter.includes(':')
        ? teacherGradeFilter.split(':')[0]
        : teacherGradeFilter;
      return [gradeId];
    }
    return [];
  }, [currentUser, teacherGradeFilter, studentGradeId, teacherTaughtGradeIds, grades]);

  const assignmentsByDate = useMemo(() => {
    const map = new Map();
    (assignments || []).forEach(a => {
      const key = a.assigned_date; // YYYY-MM-DD
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(a);
    });
    return map;
  }, [assignments]);

  const selectedDateAssignments = useMemo(() => {
    if (!selectedDate) return [];
    const key = toISODate(selectedDate);
    return assignmentsByDate.get(key) || [];
  }, [selectedDate, assignmentsByDate]);

  // Check if date is today
  const isToday = (day) => {
    if (!day) return false;
    const today = new Date();
    const dateToCheck = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return dateToCheck.toDateString() === today.toDateString();
  };

  // Check if date is selected
  const isSelected = (day) => {
    if (!day || !selectedDate) return false;
    const dateToCheck = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return dateToCheck.toDateString() === selectedDate.toDateString();
  };

  const handleNavClick = (path) => navigate(path);

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!currentUser) return;
      setError(null);
      setLoading(true);

      try {
        if (!schoolId) throw new Error('Missing school context for this user.');

        // Load grades and sections (used for teacher UI + student grade fallback)
        const [gradesData, sectionsData] = await Promise.all([
          fetchGradesForSchool(schoolId),
          fetchGradeSectionsForSchool(schoolId),
        ]);
        if (cancelled) return;
        setGrades(gradesData);
        setGradeSections(sectionsData);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load grades.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [currentUser, schoolId]);

  useEffect(() => {
    let cancelled = false;

    async function loadAssignments() {
      if (!currentUser) return;
      if (!schoolId) return;
      if (isStudent() && !studentGradeId) return;

      setError(null);
      setLoading(true);
      try {
        const data = await fetchAssignmentsInRange({
          schoolId,
          startDateISO: monthRange.startISO,
          endDateISO: monthRange.endISO,
          gradeIds: visibleGradeIds,
          // For students: pass their section to filter section-specific assignments
          sectionId: isStudent() ? studentSectionId : null,
        });
        if (cancelled) return;
        setAssignments(data);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load assignments.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadAssignments();
    return () => { cancelled = true; };
  }, [currentUser, schoolId, monthRange, visibleGradeIds, studentGradeId, studentSectionId]);

  useEffect(() => {
    let cancelled = false;

    async function loadVacations() {
      if (!currentUser) return;
      if (!schoolId) return;

      try {
        const data = await fetchVacationsInRange({
          schoolId,
          startDateISO: vacationRange.startISO,
          endDateISO: vacationRange.endISO,
        });
        if (cancelled) return;
        setVacations(data || []);
      } catch (e) {
        // If the vacations table isn't installed yet, fail silently.
        if (!cancelled) setVacations([]);
      }
    }

    loadVacations();
    return () => { cancelled = true; };
  }, [currentUser, schoolId, vacationRange]);

  // Realtime: keep vacations in sync (greys out days immediately when vacations change)
  useEffect(() => {
    if (!currentUser) return undefined;
    if (!schoolId) return undefined;

    let cancelled = false;
    let isSubscribed = false;

    const refreshVacations = async () => {
      try {
        const data = await fetchVacationsInRange({
          schoolId,
          startDateISO: vacationRange.startISO,
          endDateISO: vacationRange.endISO,
        });
        if (!cancelled) setVacations(data || []);
      } catch (e) {
        // Fail silently (e.g., table missing / realtime not enabled)
        if (!cancelled) setVacations([]);
      }
    };

    // Initial refresh (so UI is always correct even if subscription is slow)
    refreshVacations();

    // Use a stable channel name (similar to DashboardSchoolCalendar)
    const channel = supabase
      .channel(`school_vacations_changes_calendar_${schoolId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'school_vacations',
          filter: `school_id=eq.${schoolId}`,
        },
        (payload) => {
          refreshVacations();
        }
      )
      .subscribe((status) => {
        // Debug: helps confirm subscription state
        //if (status === 'SUBSCRIBED') isSubscribed = true;
      });

    // Fallback: refresh when returning to the tab (covers browsers throttling websockets/background tabs)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshVacations();
    };
    window.addEventListener('focus', refreshVacations);
    document.addEventListener('visibilitychange', onVisibility);

    // Fallback polling: ensures UI updates even if realtime is blocked by network/proxy.
    // Keep it lightweight (every 12s) and only while the component is mounted.
    const poll = window.setInterval(() => {
      // If realtime is subscribed, polling is still harmless; it just keeps things extra reliable.
      // If you want, we can change this to only poll when !isSubscribed.
      refreshVacations();
    }, 12000);

    return () => {
      cancelled = true;
      window.clearInterval(poll);
      window.removeEventListener('focus', refreshVacations);
      document.removeEventListener('visibilitychange', onVisibility);
      supabase.removeChannel(channel);
    };
  }, [currentUser, schoolId, vacationRange]);

  const days = getDaysInMonth(currentDate);

  return (
    <div className="calendar-page">
      {blockInfo && (
        <AccessGuardOverlay title={blockInfo.title} message={blockInfo.message} onAcknowledge={acknowledge} />
      )}
      {/* Header - match dashboard header style */}
      <header className="provider-page-header">
        <div className="provider-page-header-inner">
          <div
            className="header-left"
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer' }}
          >
            <img src={Logo} alt="MyDigitalSchool" className="header-logo" />
            <h1>My Digital Schools</h1>
          </div>
          <div className="header-right">
            <nav className="provider-page-nav">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  className={`provider-page-nav-item ${location.pathname === item.path ? 'active' : ''
                    }`}
                  onClick={() => handleNavClick(item.path)}
                >
                  <span className="nav-label">{item.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="page-content-wrapper">
        {/* Calendar Header */}
        <div className="calendar-header">
          <div className="calendar-title-section">
            <h1 className="calendar-title">Calendar</h1>
            <p className="calendar-subtitle">
              {isTeacher() ? 'Create and share assignments with your students' : 'See assignments shared by your teachers'}
            </p>
            {schoolName && (
              <div className="school-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <span>{schoolName}</span>
              </div>
            )}
          </div>

          <div className="calendar-controls">
            <button className="calendar-btn today-btn" onClick={goToToday}>
              Today
            </button>
            <div className="calendar-navigation">
              <button className="calendar-btn nav-btn" onClick={goToPrevious}>
                ‹
              </button>
              <h2 className="calendar-month-year">
                {headerLabel}
              </h2>
              <button className="calendar-btn nav-btn" onClick={goToNext}>
                ›
              </button>
            </div>
          </div>
        </div>

        {/* Filters / Context */}
        <div className="calendar-toolbar">
          {isTeacher() && (
            <div className="calendar-filter">
              <select
                className="calendar-select"
                value={teacherGradeFilter}
                onChange={(e) => setTeacherGradeFilter(e.target.value)}
              >
                <option value="all">All grades</option>
                {teacherGradeSectionOptions.map(opt => (
                  <option key={opt.key} value={opt.key}>{opt.label}</option>
                ))}
              </select>
              <button
                className="calendar-btn add-event-btn"
                onClick={() => openAssignmentModal(selectedDate || new Date())}
                disabled={!schoolId || selectedIsVacation}
                title={selectedIsVacation ? 'Vacation day (assignments disabled)' : (selectedDate ? 'Add assignment for selected day' : 'Add assignment')}
              >
                + Add Assignment
              </button>
            </div>
          )}

          {isStudent() && (
            <div className="calendar-filter student-context">
              <span className="calendar-pill">
                {(() => {
                  const gradeName = studentGradeId ? (gradeById.get(studentGradeId)?.name || currentUser?.grade || '—') : (currentUser?.grade || '—');
                  const section = studentSectionId ? gradeSections.find(s => s.id === studentSectionId) : null;
                  return section ? `${gradeName} ${section.name}` : gradeName;
                })()}
              </span>
            </div>
          )}
        </div>

        {/* Calendar Grid */}
        <div className="calendar-container">
          <div className="calendar-view-header">
            <button
              className="calendar-view-toggle"
              onClick={() => setCalendarView(calendarView === 'week' ? 'month' : 'week')}
              title={calendarView === 'week' ? 'Expand to month view' : 'Collapse to week view'}
            >
              {calendarView === 'week' ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <polyline points="9 21 3 21 3 15"></polyline>
                  <line x1="21" y1="3" x2="14" y2="10"></line>
                  <line x1="3" y1="21" x2="10" y2="14"></line>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="4 14 10 14 10 20"></polyline>
                  <polyline points="20 10 14 10 14 4"></polyline>
                  <line x1="14" y1="10" x2="21" y2="3"></line>
                  <line x1="3" y1="21" x2="10" y2="14"></line>
                </svg>
              )}
            </button>
          </div>
          <div className="calendar-grid">
            {/* Day headers */}
            <div className="calendar-day-headers">
              {dayNames.map(day => (
                <div key={day} className="calendar-day-header">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days - Week View */}
            {calendarView === 'week' && (
              <div className="calendar-days calendar-week-view">
                {getWeekDays(selectedDate || currentDate).map((dateObj, index) => {
                  const day = dateObj.getDate();
                  const key = toISODate(dateObj);
                  const dayAssignments = assignmentsByDate.get(key) || [];
                  const isTodayDate = dateObj.toDateString() === new Date().toDateString();
                  const isSelectedDate = selectedDate && dateObj.toDateString() === selectedDate.toDateString();
                  const isCurrentMonth = dateObj.getMonth() === currentDate.getMonth();
                  const isVacation = isVacationISO(key);
                  return (
                    <div
                      key={index}
                      className={`calendar-day week-day ${isTodayDate ? 'today' : ''} ${isSelectedDate ? 'selected' : ''} ${!isCurrentMonth ? 'other-month' : ''} ${isVacation ? 'vacation' : ''}`}
                      onClick={() => {
                        setCurrentDate(dateObj);
                        handleDateClick(dateObj);
                      }}
                      title={isVacation ? (getVacationReasonForISO(vacations, key) || 'Vacation / day off') : undefined}
                    >
                      <span className="day-number">{day}</span>
                      {dayAssignments.length > 0 && (
                        <div className="day-events">
                          {dayAssignments.slice(0, 3).map((a) => (
                            <div
                              key={a.id}
                              className="event-dot assignment"
                              title={a.title}
                            />
                          ))}
                          {dayAssignments.length > 3 && (
                            <div className="more-events">+{dayAssignments.length - 3}</div>
                          )}
                        </div>
                      )}
                      {dayAssignments.length > 0 && (
                        <div className="calendar-day-badge" aria-label={`${dayAssignments.length} assignments`}>
                          {dayAssignments.length}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Calendar days - Month View */}
            {calendarView === 'month' && (
              <div className="calendar-days">
                {days.map((day, index) => {
                  const dateObj = day ? new Date(currentDate.getFullYear(), currentDate.getMonth(), day) : null;
                  const key = dateObj ? toISODate(dateObj) : null;
                  const dayAssignments = key ? (assignmentsByDate.get(key) || []) : [];
                  const isVacation = key ? isVacationISO(key) : false;
                  return (
                    <div
                      key={index}
                      className={`calendar-day ${!day ? 'empty' : ''
                        } ${isToday(day) ? 'today' : ''
                        } ${isSelected(day) ? 'selected' : ''
                        } ${isVacation ? 'vacation' : ''}`}
                      onClick={() => handleDateClick(day)}
                      title={isVacation ? (getVacationReasonForISO(vacations, key) || 'Vacation / day off') : undefined}
                    >
                      {day && (
                        <>
                          <span className="day-number">{day}</span>
                          {dayAssignments.length > 0 && (
                            <div className="day-events">
                              {dayAssignments.slice(0, 3).map((a) => (
                                <div
                                  key={a.id}
                                  className="event-dot assignment"
                                  title={a.title}
                                />
                              ))}
                              {dayAssignments.length > 3 && (
                                <div className="more-events">+{dayAssignments.length - 3}</div>
                              )}
                            </div>
                          )}
                          {dayAssignments.length > 0 && (
                            <div className="calendar-day-badge" aria-label={`${dayAssignments.length} assignments`}>
                              {dayAssignments.length}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Selected Date Assignments */}
        {selectedDate && (
          <div className="selected-date-events">
            <h3 className="selected-date-title">
              Assignments for {selectedDate.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </h3>
            {selectedIsVacation && (
              <div className="calendar-status-line" style={{ marginBottom: '10px', color: '#6b7280' }}>
                This day is marked as a school vacation/day off{selectedVacationReason ? `: ${selectedVacationReason}` : '.'}
              </div>
            )}
            <div className="events-list">
              {selectedDateAssignments.length > 0 ? (
                selectedDateAssignments.map((a) => {
                  const grade = gradeById.get(a.grade_id);
                  const section = a.section_id ? gradeSections.find(s => s.id === a.section_id) : null;
                  const gradeName = section ? `${grade?.name || 'Grade'} ${section.name}` : (grade?.name || 'Grade');
                  const attachmentUrl = a.attachment_path ? getAttachmentPublicUrl(a.attachment_path, 'assignments') : null;
                  const teacherName = getTeacherDisplayName(a);

                  return (
                    <div key={a.id} className="assignment-card">
                      <div className="assignment-card-head">
                        <div className="assignment-title">{a.title}</div>
                        <div className="assignment-meta">
                          <span className="assignment-chip">Assignment</span>
                          {(isTeacher() && teacherGradeFilter === 'all') && (
                            <span className="assignment-chip subtle">{gradeName}</span>
                          )}
                        </div>
                      </div>

                      {a.description && <div className="assignment-description">{a.description}</div>}
                      <div className="assignment-byline">
                        Added by <strong>{teacherName}</strong>
                      </div>

                      {renderAttachmentPreview(a)}

                      <div className="assignment-actions">
                        {a.link_url && (
                          <a
                            className="assignment-link"
                            href={toAbsoluteUrl(a.link_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => openExternalUrl(a.link_url, e)}
                          >
                            Open link
                          </a>
                        )}
                        {attachmentUrl && (
                          <a
                            className="assignment-link secondary"
                            href={attachmentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => openExternalUrl(attachmentUrl, e)}
                          >
                            Download {a.attachment_name || 'attachment'}
                          </a>
                        )}
                      </div>

                      {isTeacher() && a.created_by === currentUser?.id && (
                        <div className="assignment-admin-actions">
                          <button
                            type="button"
                            className="assignment-admin-btn"
                            onClick={() => openEditAssignmentModal(a)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="assignment-admin-btn danger"
                            onClick={async () => {
                              if (a.created_by !== currentUser?.id) return;
                              const ok = window.confirm('Delete this assignment?');
                              if (!ok) return;
                              try {
                                await deleteAssignment({ assignmentId: a.id });
                                const data = await fetchAssignmentsInRange({
                                  schoolId,
                                  startDateISO: monthRange.startISO,
                                  endDateISO: monthRange.endISO,
                                  gradeIds: visibleGradeIds,
                                });
                                setAssignments(data);
                              } catch (e) {
                                alert(e.message || 'Failed to delete assignment.');
                              }
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="no-events">
                  <p>No assignments scheduled for this day</p>
                  {isTeacher() && (
                    <button
                      className="calendar-btn add-event-btn"
                      onClick={() => openAssignmentModal(selectedDate)}
                    >
                      Add Assignment
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Loading / Error */}
        {(loading || error) && (
          <div className="calendar-status">
            {loading && <div className="calendar-status-line">Loading…</div>}
            {error && <div className="calendar-status-line error">{error}</div>}
            {isStudent() && !studentGradeId && !loading && (
              <div className="calendar-status-line error">
                Your account is missing a grade assignment (no <code>grade_id</code>). Please ask the school admin to assign your grade.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Assignment Modal */}
      {showAssignmentModal && (
        <div className="event-modal-overlay" onClick={closeAssignmentModal}>
          <div className="event-modal" onClick={(e) => e.stopPropagation()}>
            <div className="event-modal-header">
              <h3>{editingAssignment ? 'Edit Assignment' : 'Add Assignment'}</h3>
              <button className="close-btn" onClick={closeAssignmentModal}>×</button>
            </div>

            <form
              className="event-form"
              onSubmit={async (e) => {
                e.preventDefault();
                setFormError(null);
                setFormSuccess(null);

                try {
                  if (!isTeacher()) {
                    throw new Error('Only teachers can create assignments.');
                  }
                  if (!schoolId) throw new Error('Missing school context.');
                  if (!currentUser?.id) throw new Error('Missing teacher id.');
                  if (!assignmentForm.assigned_date) throw new Error('Please select a date.');
                  if (!assignmentForm.title?.trim()) throw new Error('Please enter a title.');
                  if (!isValidUrl(assignmentForm.link_url)) throw new Error('Please enter a valid link URL.');
                  if (!editingAssignment && !assignmentForm.selectedKeys?.length) throw new Error('Please select at least one grade/section.');

                  setSaving(true);

                  // Convert selectedKeys to gradeAssignments format
                  const gradeAssignments = (assignmentForm.selectedKeys || []).map(key => {
                    if (key.includes(':')) {
                      const [gradeId, sectionId] = key.split(':');
                      return { gradeId, sectionId };
                    }
                    return { gradeId: key, sectionId: null };
                  });

                  let attachment = null;
                  if (attachmentFile) {
                    setUploading(true);
                    attachment = await uploadAssignmentAttachment({
                      file: attachmentFile,
                      schoolId,
                      bucket: 'assignments',
                    });
                    setUploading(false);
                  }

                  if (editingAssignment) {
                    const updates = {
                      title: assignmentForm.title.trim(),
                      description: assignmentForm.description || null,
                      link_url: toAbsoluteUrl(assignmentForm.link_url) || null,
                    };
                    if (attachment) {
                      updates.attachment_path = attachment.path;
                      updates.attachment_name = attachment.name;
                      updates.attachment_type = attachment.type;
                    } else if (removeExistingAttachment) {
                      updates.attachment_path = null;
                      updates.attachment_name = null;
                      updates.attachment_type = null;
                    }
                    await updateAssignment({ assignmentId: editingAssignment.id, updates });
                  } else {
                    await createAssignments({
                      schoolId,
                      teacherId: currentUser.id,
                      gradeAssignments,
                      assignedDateISO: assignmentForm.assigned_date,
                      title: assignmentForm.title.trim(),
                      description: assignmentForm.description,
                      linkUrl: toAbsoluteUrl(assignmentForm.link_url) || null,
                      attachment,
                    });
                  }

                  // Refresh month data
                  const data = await fetchAssignmentsInRange({
                    schoolId,
                    startDateISO: monthRange.startISO,
                    endDateISO: monthRange.endISO,
                    gradeIds: visibleGradeIds,
                  });
                  setAssignments(data);

                  setFormSuccess(editingAssignment ? 'Assignment updated.' : 'Assignment saved.');
                  closeAssignmentModal();
                } catch (err) {
                  setFormError(err.message || 'Failed to save assignment.');
                } finally {
                  setSaving(false);
                  setUploading(false);
                }
              }}
            >
              {formError && <div className="calendar-form-message error">{formError}</div>}
              {formSuccess && <div className="calendar-form-message success">{formSuccess}</div>}

              <div className="form-group">
                <label htmlFor="assignment-title">Title</label>
                <input
                  id="assignment-title"
                  type="text"
                  value={assignmentForm.title}
                  onChange={(e) => setAssignmentForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g. Math homework – Fractions"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="assignment-date">Date</label>
                  <input
                    id="assignment-date"
                    type="date"
                    value={assignmentForm.assigned_date}
                    onChange={(e) => setAssignmentForm(prev => ({ ...prev, assigned_date: e.target.value }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="assignment-link">Link (optional)</label>
                  <input
                    id="assignment-link"
                    type="url"
                    value={assignmentForm.link_url}
                    onChange={(e) => setAssignmentForm(prev => ({ ...prev, link_url: e.target.value }))}
                    placeholder="https://…"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="assignment-description">Instructions (optional)</label>
                <textarea
                  id="assignment-description"
                  value={assignmentForm.description}
                  onChange={(e) => setAssignmentForm(prev => ({ ...prev, description: e.target.value }))}
                  rows="4"
                  placeholder="Write the assignment details for students…"
                />
              </div>

              <div className="form-group">
                <label>Grades / Sections</label>
                {editingAssignment ? (
                  <div className="calendar-pill">
                    {(() => {
                      const grade = gradeById.get(editingAssignment.grade_id);
                      const section = editingAssignment.section_id
                        ? gradeSections.find(s => s.id === editingAssignment.section_id)
                        : null;
                      return section ? `${grade?.name || 'Grade'} ${section.name}` : (grade?.name || 'Grade');
                    })()}
                  </div>
                ) : (
                  <div className="grade-checkbox-grid">
                    {teacherGradeSectionOptions.length > 0 ? (
                      teacherGradeSectionOptions.map((opt) => {
                        const checked = assignmentForm.selectedKeys.includes(opt.key);
                        return (
                          <label
                            key={opt.key}
                            className={`cal-gradeChoice ${checked ? 'isChecked' : ''} ${opt.isReadOnly ? 'isReadOnly' : ''}`}
                          >
                            <input
                              className="cal-gradeChoice__input"
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                setAssignmentForm(prev => {
                                  const next = new Set(prev.selectedKeys);
                                  if (e.target.checked) next.add(opt.key);
                                  else next.delete(opt.key);
                                  return { ...prev, selectedKeys: Array.from(next) };
                                });
                              }}
                            />
                            <span className="cal-gradeChoice__box" aria-hidden="true" />
                            <span className="cal-gradeChoice__label">{opt.label}</span>
                          </label>
                        );
                      })
                    ) : (
                      <div className="no-grades-message">
                        No grades/sections assigned. Please contact your administrator.
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Attachment (optional)</label>

                {editingAssignment?.attachment_path && !removeExistingAttachment && (
                  <div className="existing-attachment-area">
                    <div className="existing-file-info">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                        <polyline points="13 2 13 9 20 9" />
                      </svg>
                      <span className="existing-file-name">{editingAssignment.attachment_name || 'Current file'}</span>
                    </div>
                    <button
                      type="button"
                      className="remove-existing-btn"
                      onClick={() => setRemoveExistingAttachment(true)}
                    >
                      Remove
                    </button>
                  </div>
                )}

                <div
                  className={`file-drop-zone ${isDragging ? 'is-dragging' : ''} ${attachmentFile ? 'has-file' : ''}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                    const file = e.dataTransfer.files?.[0];
                    if (file) {
                      setAttachmentFile(file);
                      setRemoveExistingAttachment(true);
                    }
                  }}
                  onClick={() => document.getElementById('assignment-file').click()}
                >
                  <input
                    id="assignment-file"
                    type="file"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setAttachmentFile(file);
                        setRemoveExistingAttachment(true);
                      }
                    }}
                    disabled={saving || uploading}
                  />

                  {!attachmentFile ? (
                    <div className="drop-zone-content">
                      <div className="drop-zone-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                      </div>
                      <div className="drop-zone-text">
                        <strong>Click to upload</strong> or drag and drop
                      </div>
                      <div className="drop-zone-hint">PDF, DOCX, PNG or JPG (max. 10MB)</div>
                    </div>
                  ) : (
                    <div className="drop-zone-file">
                      <div className="file-info">
                        <div className="file-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                            <polyline points="13 2 13 9 20 9" />
                          </svg>
                        </div>
                        <div className="file-name-container">
                          <div className="file-name">{attachmentFile.name}</div>
                          <div className="file-size">{(attachmentFile.size / (1024 * 1024)).toFixed(2)} MB</div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="remove-file-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAttachmentFile(null);
                        }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="calendar-btn cancel-btn" onClick={closeAssignmentModal} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="calendar-btn save-btn" disabled={saving || uploading}>
                  {uploading ? 'Uploading…' : (saving ? 'Saving…' : 'Save Assignment')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Day Overlay */}
      {showDayOverlay && (
        <div className="event-modal-overlay" onClick={() => setShowDayOverlay(false)}>
          <div className="event-modal assignment-day-modal" onClick={(e) => e.stopPropagation()}>
            <div className="event-modal-header">
              <h3>
                {dayOverlayDate ? dayOverlayDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'Assignments'}
              </h3>
              <button className="close-btn" onClick={() => setShowDayOverlay(false)}>×</button>
            </div>

            <div className="event-form">
              {(dayOverlayDate ? (assignmentsByDate.get(toISODate(dayOverlayDate)) || []) : []).map((a) => {
                const grade = gradeById.get(a.grade_id);
                const section = a.section_id ? gradeSections.find(s => s.id === a.section_id) : null;
                const gradeName = section ? `${grade?.name || 'Grade'} ${section.name}` : (grade?.name || 'Grade');
                const attachmentUrl = a.attachment_path ? getAttachmentPublicUrl(a.attachment_path, 'assignments') : null;
                const teacherName = getTeacherDisplayName(a);
                return (
                  <div key={a.id} className="assignment-card">
                    <div className="assignment-card-head">
                      <div className="assignment-title">{a.title}</div>
                      <div className="assignment-meta">
                        <span className="assignment-chip subtle">{gradeName}</span>
                      </div>
                    </div>

                    {a.description && <div className="assignment-description">{a.description}</div>}
                    <div className="assignment-byline">
                      Added by <strong>{teacherName}</strong>
                    </div>

                    {renderAttachmentPreview(a)}

                    <div className="assignment-actions">
                      {a.link_url && (
                        <a
                          className="assignment-link"
                          href={toAbsoluteUrl(a.link_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => openExternalUrl(a.link_url, e)}
                        >
                          Open link
                        </a>
                      )}
                      {attachmentUrl && (
                        <a
                          className="assignment-link secondary"
                          href={attachmentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => openExternalUrl(attachmentUrl, e)}
                        >
                          Download {a.attachment_name || 'attachment'}
                        </a>
                      )}
                    </div>

                    {isTeacher() && a.created_by === currentUser?.id && (
                      <div className="assignment-admin-actions">
                        <button type="button" className="assignment-admin-btn" onClick={() => openEditAssignmentModal(a)}>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="assignment-admin-btn danger"
                          onClick={async () => {
                            if (a.created_by !== currentUser?.id) return;
                            const ok = window.confirm('Delete this assignment?');
                            if (!ok) return;
                            try {
                              await deleteAssignment({ assignmentId: a.id });
                              const data = await fetchAssignmentsInRange({
                                schoolId,
                                startDateISO: monthRange.startISO,
                                endDateISO: monthRange.endISO,
                                gradeIds: visibleGradeIds,
                              });
                              setAssignments(data);
                              const key = dayOverlayDate ? toISODate(dayOverlayDate) : null;
                              if (key) {
                                const nextDay = (data || []).filter(r => r.assigned_date === key);
                                if (!nextDay.length) setShowDayOverlay(false);
                              }
                            } catch (e) {
                              alert(e.message || 'Failed to delete assignment.');
                            }
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {isTeacher() && (
                <div style={{ marginTop: '1rem' }}>
                  <button className="calendar-btn add-event-btn" onClick={() => openAssignmentModal(dayOverlayDate || selectedDate || new Date())}>
                    + Add Assignment
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
